import React, { useImperativeHandle, forwardRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  NumberInput,
  NumberInputField,
  Text,
  Box,
  SimpleGrid,
  Heading,
  Flex,
  Switch,
  useColorModeValue,
  HStack,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { FaBox } from 'react-icons/fa';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createProduct, updateProduct } from '../../services/productService';
import { getGateways } from '../../services/gatewayService';
import ProductVariantOrgChart from '../ProductVariantOrgChart';
import toast from 'react-hot-toast';

const defaultLogicalVariant = () => ({
  accountingCode: '',
  stock: 0,
  price: 0,
  syncPrice: true,
});

const defaultVariant = () => ({
  variantId: '',
  name: '',
  stock: 0,
  price: 0,
  pricePercent: null,
  sku: '',
  accountingCode: '',
  logicalVariantId: '',
});

const defaultGatewayRef = () => ({
  gateway: '',
  productId: '',
  pricePercent: null,
  syncPrice: true,
  variants: [],
});

const ProductForm = forwardRef(({ product, onSuccess, onDirty }, ref) => {
  const queryClient = useQueryClient();

  const inputBg = useColorModeValue('gray.50', 'gray.900');
  const sectionBg = useColorModeValue('white', 'whiteAlpha.50');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'white');
  const inputFocusBg = useColorModeValue('white', 'gray.800');
  const inputHoverBorder = useColorModeValue('gray.300', 'gray.600');
  const activeStatusBg = useColorModeValue('green.50', 'whiteAlpha.50');
  const activeStatusBorder = useColorModeValue('green.100', 'whiteAlpha.200');

  const commonInputProps = {
    bg: inputBg,
    border: '1px solid',
    borderColor: 'transparent',
    borderRadius: 'xl',
    _focus: { borderColor: 'blue.400', boxShadow: '0 0 0 1px #3b82f6', bg: inputFocusBg },
    _hover: { borderColor: inputHoverBorder },
    transition: 'all 0.2s',
  };

  const [logicalVariants, setLogicalVariants] = React.useState(
    product?.logicalVariants?.length > 0
      ? product.logicalVariants.map((lv) => ({
          _id: lv._id,
          accountingCode: lv.accountingCode ?? '',
          stock: lv.stock ?? 0,
          price: lv.price ?? 0,
          syncPrice: lv.syncPrice !== false,
        }))
      : []
  );

  const [gatewayRefs, setGatewayRefs] = React.useState(
    product?.gatewayRefs?.length > 0
      ? product.gatewayRefs.map((gref) => ({
          _id: gref._id,
          gateway: gref.gateway?._id || gref.gateway || '',
          productId: gref.productId != null ? String(gref.productId) : '',
          pricePercent: gref.pricePercent == null || gref.pricePercent === '' ? null : gref.pricePercent,
          syncPrice: gref.syncPrice !== false,
          variants: (gref.variants || []).map((v) => ({
            _id: v._id,
            variantId: v.variantId != null ? String(v.variantId) : '',
            name: v.name || '',
            stock: v.stock ?? 0,
            price: v.price ?? 0,
            pricePercent: v.pricePercent ?? null,
            syncPrice: v.syncPrice !== false,
            sku: v.sku || '',
            accountingCode: v.accountingCode ?? '',
            logicalVariantId: v.logicalVariantId?._id ?? v.logicalVariantId ?? '',
          })),
        }))
      : []
  );

  const { data: gatewaysData } = useQuery(['gateways'], getGateways);
  const gateways = gatewaysData?.data || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: product?.name || '',
      accountingCode: product?.accountingCode ?? '',
      stock: product?.stock ?? 0,
      image: product?.image || '',
      isActive: product?.isActive !== false,
      syncPrice: product?.syncPrice !== false,
    },
    mode: 'onChange',
  });

  const productName = watch('name');
  const productStock = watch('stock');

  useEffect(() => {
    if (product?.logicalVariants?.length > 0 && logicalVariants.length === 0) {
      setLogicalVariants(
        product.logicalVariants.map((lv) => ({
          _id: lv._id,
          accountingCode: lv.accountingCode ?? '',
          stock: lv.stock ?? 0,
          price: lv.price ?? 0,
          syncPrice: lv.syncPrice !== false,
        }))
      );
    }
  }, [product?.logicalVariants]);

  useEffect(() => {
    if (product?.gatewayRefs?.length > 0 && gatewayRefs.length === 0) {
      setGatewayRefs(
        product.gatewayRefs.map((gref) => ({
          _id: gref._id,
          gateway: gref.gateway?._id || gref.gateway || '',
          productId: gref.productId != null ? String(gref.productId) : '',
          pricePercent: gref.pricePercent == null || gref.pricePercent === '' ? null : gref.pricePercent,
          syncPrice: gref.syncPrice !== false,
          variants: (gref.variants || []).map((v) => ({
            _id: v._id,
            variantId: v.variantId != null ? String(v.variantId) : '',
            name: v.name || '',
            stock: v.stock ?? 0,
            price: v.price ?? 0,
            pricePercent: v.pricePercent ?? null,
            syncPrice: v.syncPrice !== false,
            sku: v.sku || '',
            accountingCode: v.accountingCode ?? '',
            logicalVariantId: v.logicalVariantId?._id ?? v.logicalVariantId ?? '',
          })),
        }))
      );
    }
  }, [product?.gatewayRefs]);

  const createProductMutation = useMutation(createProduct, {
    onSuccess: () => {
      toast.success('محصول با موفقیت اضافه شد');
      queryClient.invalidateQueries(['products']);
      if (onSuccess) onSuccess();
    },
    onError: (error) => toast.error(error.response?.data?.message || 'خطا در افزودن محصول'),
  });

  const updateProductMutation = useMutation(
    ({ productId, productData }) => updateProduct(productId, productData),
    {
      onSuccess: () => {
        toast.success('محصول با موفقیت بروزرسانی شد');
        queryClient.invalidateQueries(['products']);
        if (onSuccess) onSuccess();
      },
      onError: (error) => toast.error(error.response?.data?.message || 'خطا در بروزرسانی محصول'),
    }
  );

  const addLogicalVariant = () => { setLogicalVariants((prev) => [...prev, defaultLogicalVariant()]); onDirty?.(); };
  const removeLogicalVariant = (idx) => {
    const removedKey = logicalVariants[idx]?._id || `new-${idx}`;
    setLogicalVariants((prev) => prev.filter((_, i) => i !== idx));
    setGatewayRefs((prev) =>
      prev.map((gref) => ({
        ...gref,
        variants: (gref.variants || []).map((v) =>
          String(v.logicalVariantId) === String(removedKey) ? { ...v, logicalVariantId: '' } : v
        ),
      }))
    );
    onDirty?.();
  };
  const setLogicalVariantField = (idx, field, value) => {
    setLogicalVariants((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    onDirty?.();
  };

  const addGatewayRef = () => { setGatewayRefs((prev) => [...prev, defaultGatewayRef()]); onDirty?.(); };
  const removeGatewayRef = (refIndex) => { setGatewayRefs((prev) => prev.filter((_, i) => i !== refIndex)); onDirty?.(); };
  const setGatewayRefField = (refIndex, field, value) => {
    setGatewayRefs((prev) => {
      const next = [...prev];
      next[refIndex] = { ...next[refIndex], [field]: value };
      return next;
    });
    onDirty?.();
  };

  const addVariant = (refIndex, logicalVariantId = '') => {
    setGatewayRefs((prev) => {
      const next = [...prev];
      const variant = defaultVariant();
      if (logicalVariantId) variant.logicalVariantId = logicalVariantId;
      next[refIndex] = {
        ...next[refIndex],
        variants: [...(next[refIndex].variants || []), variant],
      };
      return next;
    });
    onDirty?.();
  };

  const removeVariant = (refIndex, variantIndex) => {
    setGatewayRefs((prev) => {
      const next = [...prev];
      next[refIndex] = {
        ...next[refIndex],
        variants: (next[refIndex].variants || []).filter((_, i) => i !== variantIndex),
      };
      return next;
    });
    onDirty?.();
  };

  const updateVariant = (refIndex, variantIndex, field, value) => {
    setGatewayRefs((prev) => {
      const next = [...prev];
      const variants = [...(next[refIndex].variants || [])];
      variants[variantIndex] = { ...variants[variantIndex], [field]: value };
      next[refIndex] = { ...next[refIndex], variants };
      return next;
    });
    onDirty?.();
  };

  const handleFormSubmit = (data) => {
    const cleanData = {
      name: data.name,
      accountingCode:
        data.accountingCode && String(data.accountingCode).trim()
          ? String(data.accountingCode).trim()
          : null,
      stock: Math.max(0, parseInt(data.stock, 10) || 0),
      image: data.image || '',
      isActive: data.isActive !== false,
      syncPrice: data.syncPrice !== false,
      logicalVariants: logicalVariants.map((lv) => ({
        ...(lv._id && { _id: lv._id }),
        accountingCode: lv.accountingCode != null ? String(lv.accountingCode).trim() : '',
        stock: Math.max(0, parseInt(lv.stock, 10) || 0),
        price: Math.max(0, Number(lv.price) || 0),
        syncPrice: lv.syncPrice !== false,
      })),
      gatewayRefs: gatewayRefs
        .map((gref) => ({
          gateway: gref.gateway || undefined,
          productId: gref.productId && String(gref.productId).trim() ? String(gref.productId).trim() : '',
          pricePercent:
            gref.pricePercent == null || gref.pricePercent === '' || !Number.isFinite(Number(gref.pricePercent))
              ? null
              : Number(gref.pricePercent),
          syncPrice: gref.syncPrice !== false,
          variants: (gref.variants || []).map((v) => ({
            ...(v._id && { _id: v._id }),
            variantId: v.variantId && String(v.variantId).trim() ? Number(v.variantId) : 0,
            name: v.name || '',
            stock: Math.max(0, parseInt(v.stock, 10) || 0),
            price: Math.max(0, Number(v.price) || 0),
            pricePercent:
              v.pricePercent == null || v.pricePercent === '' || !Number.isFinite(Number(v.pricePercent))
                ? null
                : Number(v.pricePercent),
            syncPrice: v.syncPrice !== false,
            sku: v.sku || '',
            accountingCode: v.accountingCode != null ? String(v.accountingCode).trim() : '',
            logicalVariantId:
              v.logicalVariantId && String(v.logicalVariantId).trim() && !String(v.logicalVariantId).startsWith('new-')
                ? v.logicalVariantId
                : null,
          })),
        }))
        .filter((gref) => gref.gateway && gref.productId && String(gref.productId).trim()),
    };

    if (product) updateProductMutation.mutate({ productId: product._id, productData: cleanData });
    else createProductMutation.mutate(cleanData);
  };

  useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(handleFormSubmit)(),
  }));

  const isEditMode = !!product;

  return (
    <Box as="form" onSubmit={handleSubmit(handleFormSubmit)} onChange={() => onDirty?.()} dir="rtl" pb={4} maxW="100%" minW={0}>
      {isEditMode && (
        <Alert
          status="info"
          borderRadius="xl"
          mb={6}
          bg="blue.50"
          borderWidth="1px"
          borderColor="blue.200"
          _dark={{ bg: 'blue.900', borderColor: 'blue.700' }}
        >
          <AlertIcon color="blue.500" />
          <AlertDescription fontSize="sm">
            برای تغییر موجودی از لیست محصولات، گزینه <b>«موجودی جدید»</b> را استفاده کنید.
            در چارت زیر، هر <b>سلول</b> نشان‌دهنده اتصال یک تنوع منطقی به یک درگاه است.
          </AlertDescription>
        </Alert>
      )}

      {/* اطلاعات پایه — فشرده */}
      <Box
        bg={sectionBg}
        p={5}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        shadow="sm"
        mb={6}
      >
        <HStack mb={4}>
          <Flex w="36px" h="36px" borderRadius="lg" bg="blue.50" color="blue.500" align="center" justify="center">
            <Icon as={FaBox} boxSize={4} />
          </Flex>
          <Heading size="sm" color={textColor}>
            اطلاعات پایه
          </Heading>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <FormControl isInvalid={errors.name} gridColumn={{ lg: 'span 2' }}>
            <FormLabel fontSize="sm" fontWeight="bold">
              نام محصول
            </FormLabel>
            <Input
              {...commonInputProps}
              {...register('name', { required: 'نام محصول الزامی است' })}
              placeholder="مثال: گوشی سامسونگ مدل S24"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="bold">
              کد حسابداری (SKU)
            </FormLabel>
            <Input
              {...commonInputProps}
              dir="ltr"
              textAlign="right"
              {...register('accountingCode')}
              placeholder="SAM-S24"
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="bold">
              موجودی اولیه
            </FormLabel>
            <NumberInput
              min={0}
              defaultValue={product?.stock ?? 0}
              onChange={(val) => setValue('stock', parseInt(val, 10) || 0)}
              isDisabled={isEditMode}
            >
              <NumberInputField {...commonInputProps} {...register('stock')} />
            </NumberInput>
          </FormControl>

          <FormControl gridColumn={{ lg: 'span 2' }}>
            <FormLabel fontSize="sm" fontWeight="bold">
              لینک تصویر
            </FormLabel>
            <Input
              {...commonInputProps}
              dir="ltr"
              textAlign="right"
              {...register('image')}
              placeholder="https://..."
            />
          </FormControl>

          <Box
            bg={activeStatusBg}
            p={3}
            borderRadius="xl"
            border="1px solid"
            borderColor={activeStatusBorder}
            gridColumn={{ lg: 'span 1' }}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="bold" fontSize="sm">
                  وضعیت کالا
                </Text>
                <Text fontSize="xs" color={mutedColor}>
                  فعال/غیرفعال بودن کالا در سیستم
                </Text>
              </Box>
              <Switch {...register('isActive')} defaultChecked={product?.isActive !== false} colorScheme="green" />
            </Flex>
          </Box>

          <Box
            bg={sectionBg}
            p={3}
            borderRadius="xl"
            border="1px solid"
            borderColor={borderColor}
            gridColumn={{ lg: 'span 1' }}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="bold" fontSize="sm">
                  سیستم قیمت متناسب
                </Text>
                <Text fontSize="xs" color={mutedColor}>
                  تبدیل خودکار قیمت برای این محصول
                </Text>
              </Box>
              <Switch {...register('syncPrice')} defaultChecked={product?.syncPrice !== false} colorScheme="teal" />
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>

      {/* چارت سازمانی — بخش اصلی */}
      <Box mb={2} maxW="100%" minW={0} overflow="hidden">
        <Text fontSize="lg" fontWeight="bold" color={textColor} mb={1}>
          نقشه اتصال تنوع‌ها
        </Text>
        <Text fontSize="sm" color={mutedColor} mb={4}>
          ساختار چارت سازمانی: محصول در بالا، تنوع‌های منطقی در ردیف‌ها، و هر ستون یک درگاه فروش.
          در تقاطع هر ردیف و ستون، تنوع آن درگاه به آن تنوع منطقی متصل می‌شود.
        </Text>

        <ProductVariantOrgChart
          productName={productName}
          productStock={productStock}
          productSyncPrice={watch('syncPrice')}
          logicalVariants={logicalVariants}
          gatewayRefs={gatewayRefs}
          gateways={gateways}
          isEditMode={isEditMode}
          onAddLogicalVariant={addLogicalVariant}
          onRemoveLogicalVariant={removeLogicalVariant}
          onLogicalVariantField={setLogicalVariantField}
          onAddGatewayRef={addGatewayRef}
          onRemoveGatewayRef={removeGatewayRef}
          onGatewayRefField={setGatewayRefField}
          onAddVariant={addVariant}
          onUpdateVariant={updateVariant}
          onRemoveVariant={removeVariant}
        />
      </Box>
    </Box>
  );
});

ProductForm.displayName = 'ProductForm';

export default ProductForm;
